/**
 * P-18 (D-134..D-138, D-140, D-141) — the pure rules of contacts and their
 * portal access, shared by the three places that show the ONE contact form
 * (the Contacts page, the Contacts tab of a customer, "גישה חדשה" on the
 * Portal-access page) and by the customer form / lead conversion.
 * No React, no i18n instance: tested in src/utils/__tests__/contactAccessModel.test.ts.
 */

/** The three contact types (D-28, D-135, D-140) — `contacts.contact_role`. */
export const CONTACT_TYPES = ['customer_manager', 'primary_contact', 'customer_employee'] as const;
export type ContactType = typeof CONTACT_TYPES[number];

/**
 * i18n keys of the type labels. "מנהל מטעם הלקוח" lives in ONE key
 * (`terms.customerSideManager`) that every other label nests (D-140).
 */
export const CONTACT_TYPE_LABEL_KEY: Record<ContactType, string> = {
  customer_manager: 'contacts.contactRole.customer_manager',
  primary_contact: 'contacts.contactRole.primary_contact',
  customer_employee: 'contacts.contactRole.customer_employee',
};

/** The hint under each access level (what that person sees in the portal, D-100). */
export const ACCESS_LEVEL_HINT_KEY: Record<ContactType, string> = {
  customer_manager: 'portalAccess.accessLevelManagerHint',
  primary_contact: 'portalAccess.accessLevelPrimaryHint',
  customer_employee: 'portalAccess.accessLevelEmployeeHint',
};

export function isContactType(v: unknown): v is ContactType {
  return typeof v === 'string' && (CONTACT_TYPES as readonly string[]).includes(v);
}

/** D-135: the access level is pre-selected from the contact's type (same three values). */
export function defaultAccessLevel(type: ContactType | '' | null | undefined): ContactType {
  return isContactType(type) ? type : 'customer_employee';
}

/**
 * The level a stored grant SHOWS. The rank (`portal_role`) decides manager vs
 * employee; for the manager rank the contact's type tells "מנהל מטעם הלקוח"
 * from "איש קשר ראשי" (both share the rank, D-100). No contact type and no
 * known rank → null ("לא מסווג").
 */
export function grantLevel(portalRole: string | null | undefined, contactRole: string | null | undefined): ContactType | null {
  if (portalRole === 'customer_portal_manager') {
    return contactRole === 'primary_contact' ? 'primary_contact' : 'customer_manager';
  }
  if (portalRole === 'customer_employee' || portalRole === 'customer_viewer') return 'customer_employee';
  return isContactType(contactRole) ? contactRole : null;
}

export const PASSWORD_MIN = 8;
export function isStrongPassword(pw: string): boolean {
  return pw.length >= PASSWORD_MIN && /[A-Z]/.test(pw) && /[0-9]/.test(pw) && /[^A-Za-z0-9]/.test(pw);
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Why the access block cannot be saved yet (an i18n key), in the order the
 * fields appear: e-mail, level, password. `undefined` = complete.
 */
export function accessBlocker(a: { email: string; level?: ContactType | '' | null; password: string; needsLevel?: boolean }): string | undefined {
  return accessBlockers(a)[0];
}

/**
 * W-08c: EVERY reason the access block cannot be saved (i18n keys), at most
 * one per field, in screen order: e-mail, level, password. `accessBlocker` = the first.
 */
export function accessBlockers(a: { email: string; level?: ContactType | '' | null; password: string; needsLevel?: boolean }): string[] {
  const out: string[] = [];
  if (!a.email.trim()) out.push('contacts.accessNeedsEmail');
  else if (!EMAIL_RE.test(a.email.trim())) out.push('validation.invalidEmail');
  if (a.needsLevel !== false && !a.level) out.push('contacts.accessNeedsType');
  if (!a.password) out.push('contacts.accessNeedsPassword');
  else if (!isStrongPassword(a.password)) out.push('contacts.passwordWeak');
  return out;
}

/**
 * D-135: the access e-mail follows the contact's e-mail until it is edited
 * by hand (then it is the user's choice and stays).
 */
export function followEmail(prevSource: string, nextSource: string, current: string): string {
  return current === prevSource ? nextSource : current;
}

/** D-271: the free-text fields of a contact, sent trimmed (create AND edit). */
export const CONTACT_TEXT_FIELDS = ['name', 'email', 'phone', 'mobile', 'role_title', 'notes'] as const;
export function trimContactText<T extends Record<string, unknown>>(form: T): T {
  const out: Record<string, unknown> = { ...form };
  for (const k of CONTACT_TEXT_FIELDS) if (typeof out[k] === 'string') out[k] = (out[k] as string).trim();
  return out as T;
}

/** The body of POST /api/contacts/with-access for the shared form. */
export function contactCreatePayload(form: Record<string, unknown> & { contact_role: ContactType | '' }, access: {
  enabled: boolean; email: string; level: ContactType; password: string; approveTickets: boolean;
  /** P-21 (D-152): the access checkboxes, when the form shows them. */
  perms?: Record<'view_projects' | 'view_documents' | 'view_hours' | 'view_billing', boolean>;
} | null): Record<string, unknown> {
  // D-271: a pasted e-mail may carry stray spaces; the server refuses those.
  const base = { ...trimContactText(form), contact_role: form.contact_role || null };
  if (!access || !access.enabled) return base;
  return {
    ...base,
    password: access.password,
    access_type: access.level,
    access_email: access.email.trim(),
    approve_tickets: access.approveTickets,
    ...(access.perms ? { permissions: { ...access.perms, approve_tickets: access.approveTickets } } : {}),
  };
}

/**
 * D-138 / D-141 — the optional `portal_access` part of the customer-creation
 * and lead-conversion requests. The server creates the contact (named after
 * the customer, "מנהל מטעם הלקוח" + איש קשר ראשי), its login and a manager
 * grant in the same transaction, and sends the welcome e-mail.
 */
export function customerPortalAccessPayload(a: { enabled: boolean; email: string; password: string }):
  { portal_access: { email: string; password: string } } | Record<string, never> {
  return a.enabled ? { portal_access: { email: a.email.trim(), password: a.password } } : {};
}

/**
 * D-250 - the server's 409 CONTACT_EMAIL_EXISTS (same e-mail on an active contact of the
 * same customer) -> the existing contact to offer; any other error -> null.
 */
export function duplicateContactFromError(err: unknown): { id: string; name: string; hasAccess: boolean } | null {
  const e = (err as { error?: { error_code?: string; details?: { contact_id?: string; contact_name?: string; has_access?: boolean } } } | null)?.error;
  if (e?.error_code !== 'CONTACT_EMAIL_EXISTS' || !e.details?.contact_id) return null;
  return { id: e.details.contact_id, name: e.details.contact_name || '', hasAccess: e.details.has_access === true };
}

/**
 * D-271 - which field of the contact form a server rejection is about, so the
 * form can mark that field (and not only say "some values are invalid").
 * The server names the field in its Hebrew sentence; the map is keyed on that
 * wording. Anything else -> null (the general line under the form stays).
 */
export type ContactServerField = 'contact-email' | 'contact-name' | 'contact-phone' | 'contact-mobile' | 'contact-role-title' | 'contact-notes'
  | 'access-email' | 'access-password' | 'access-perms' | 'organization';
const SERVER_FIELD_RULES: ReadonlyArray<[RegExp, ContactServerField]> = [
  [/האימייל של איש הקשר/, 'contact-email'],
  [/שם איש הקשר|שם הוא שדה חובה/, 'contact-name'],
  [/טלפון/, 'contact-phone'],
  [/נייד/, 'contact-mobile'],
  [/תפקיד לא תקין/, 'contact-role-title'],
  [/הערות/, 'contact-notes'],
  [/מאשר קריאות|ההרשאות/, 'access-perms'],
  [/אימייל לגישה|דורשת גם אימייל/, 'access-email'],
  [/סיסמה/, 'access-password'],
  [/יש לבחור לקוח/, 'organization'],
];
export function contactServerFieldIssue(err: unknown): { field: ContactServerField; message: string } | null {
  const e = (err as { error?: { message?: string } | string } | null)?.error;
  const message = (typeof e === 'object' && e ? e.message : typeof e === 'string' ? e : '') || '';
  if (!message) return null;
  for (const [re, field] of SERVER_FIELD_RULES) if (re.test(message)) return { field, message };
  return null;
}
